import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { X, Download, ZoomIn, ZoomOut, RefreshCw } from 'lucide-react';
import { toast } from 'sonner';

const DocumentPreviewModal = ({ document, isOpen, onClose, onDownload }) => {
  const [zoom, setZoom] = useState(100);
  const [imageError, setImageError] = useState(false);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (document && isOpen) {
      setLoading(true);
      setImageError(false);
      
      const token = localStorage.getItem('token');
      const url = `${process.env.REACT_APP_BACKEND_URL}/api/documents/preview/${document.id}`;
      
      fetch(url, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Accept': '*/*'
        },
        credentials: 'include'
      })
      .then(response => {
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        return response.blob();
      })
      .then(blob => {
        const objectUrl = URL.createObjectURL(blob);
        setPreviewUrl(objectUrl);
        setImageError(false);
        setLoading(false);
      })
      .catch(error => {
        console.error('Preview error:', error);
        setImageError(true);
        setLoading(false);
        toast.error('Failed to load preview');
      });
    }
    
    return () => {
      if (previewUrl) {
        URL.revokeObjectURL(previewUrl);
      }
    };
  }, [document?.id, isOpen]);

  if (!document) return null;

  const isImage = document.file_type?.startsWith('image/');
  const isPDF = document.file_type === 'application/pdf' || document.file_name?.endsWith('.pdf');
  const isPreviewable = isImage || isPDF;

  const handleZoomIn = () => setZoom(prev => Math.min(prev + 25, 200));
  const handleZoomOut = () => setZoom(prev => Math.max(prev - 25, 50));
  
  const handleRetry = () => {
    setLoading(true);
    setImageError(false);
    const token = localStorage.getItem('token');
    const url = `${process.env.REACT_APP_BACKEND_URL}/api/documents/preview/${document.id}`;
    
    fetch(url, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Accept': '*/*'
      },
      credentials: 'include'
    })
    .then(response => response.blob())
    .then(blob => {
      if (previewUrl) URL.revokeObjectURL(previewUrl);
      const objectUrl = URL.createObjectURL(blob);
      setPreviewUrl(objectUrl);
      setImageError(false);
      setLoading(false);
    })
    .catch(error => {
      console.error('Retry preview error:', error);
      setImageError(true);
      setLoading(false);
    });
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-5xl h-[90vh] flex flex-col">
        <DialogHeader>
          <div className="flex items-center justify-between">
            <DialogTitle style={{ fontFamily: 'Outfit, sans-serif' }}>
              Document Preview
            </DialogTitle>
            <div className="flex items-center gap-2">
              {isImage && previewUrl && !imageError && (
                <>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={handleZoomOut}
                    data-testid="zoom-out-button"
                  >
                    <ZoomOut className="h-4 w-4" />
                  </Button>
                  <span className="text-sm text-slate-600">{zoom}%</span>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={handleZoomIn}
                    data-testid="zoom-in-button"
                  >
                    <ZoomIn className="h-4 w-4" />
                  </Button>
                </>
              )}
              <Button
                size="sm"
                onClick={() => onDownload(document.id, document.file_name)}
                data-testid="preview-download-button"
              >
                <Download className="h-4 w-4 mr-2" />
                Download
              </Button>
              <Button
                size="sm"
                variant="ghost"
                onClick={onClose}
                data-testid="close-preview-button"
              >
                <X className="h-4 w-4" />
              </Button>
            </div>
          </div>
          <div className="text-sm text-slate-600 mt-2" style={{ fontFamily: 'Public Sans, sans-serif' }}>
            {document.file_name} ({document.file_type || 'unknown'})
          </div>
        </DialogHeader>

        <div className="flex-1 overflow-auto bg-slate-50 rounded-lg p-4 mt-4">
          {isPreviewable ? (
            <>
              {isImage && (
                <div className="flex items-center justify-center min-h-full">
                  {loading && !imageError && (
                    <div className="text-center">
                      <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-slate-900 mx-auto mb-4"></div>
                      <p className="text-slate-600">Loading image...</p>
                    </div>
                  )}
                  {previewUrl && !imageError && !loading && (
                    <img
                      src={previewUrl}
                      alt={document.file_name}
                      style={{ 
                        width: `${zoom}%`,
                        maxWidth: 'none',
                        height: 'auto'
                      }}
                      className="rounded-lg shadow-lg"
                      onError={() => {
                        setImageError(true);
                        toast.error('Failed to display image');
                      }}
                    />
                  )}
                  {imageError && (
                    <div className="text-center">
                      <p className="text-red-600 mb-4">Failed to load image</p>
                      <div className="flex gap-2 justify-center">
                        <Button onClick={handleRetry} variant="outline">
                          <RefreshCw className="h-4 w-4 mr-2" />
                          Retry
                        </Button>
                        <Button onClick={() => onDownload(document.id, document.file_name)}>
                          <Download className="h-4 w-4 mr-2" />
                          Download Instead
                        </Button>
                      </div>
                    </div>
                  )}
                </div>
              )}
              {isPDF && (
                <div className="h-full">
                  {loading && (
                    <div className="flex items-center justify-center h-full">
                      <div className="text-center">
                        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-slate-900 mx-auto mb-4"></div>
                        <p className="text-slate-600">Loading PDF...</p>
                      </div>
                    </div>
                  )}
                  {previewUrl && !loading && (
                    <iframe
                      src={previewUrl}
                      className="w-full h-full min-h-[600px] rounded-lg border-0"
                      title={document.file_name}
                      onError={() => {
                        setImageError(true);
                        toast.error('Failed to load PDF');
                      }}
                    />
                  )}
                </div>
              )}
            </>
          ) : (
            <div className="flex flex-col items-center justify-center h-full text-center p-8">
              <div className="mb-4">
                <svg
                  className="w-16 h-16 text-slate-400 mx-auto"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z"
                  />
                </svg>
              </div>
              <h3 className="text-lg font-semibold text-slate-700 mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
                Preview Not Available
              </h3>
              <p className="text-slate-600 mb-4" style={{ fontFamily: 'Public Sans, sans-serif' }}>
                This file type (.{document.file_name?.split('.').pop()}) cannot be previewed in the browser.
                <br />
                Please download the file to view it.
              </p>
              <Button onClick={() => onDownload(document.id, document.file_name)}>
                <Download className="h-4 w-4 mr-2" />
                Download File
              </Button>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default DocumentPreviewModal;
